import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import GoogleSvg from "../Google.svg";
import AppleSvg from "../Apple.svg";
import { UMSSBlue, UMSSBlueDark } from "../utils/colors";
import { bookAppointment } from "../firebaseBooking";
const SERVICE_TYPE_LABELS = { primary_care: 'Primary Care', mental_health: 'Mental Health', wellness: 'Wellness / Vaccines' };
const getServiceTypeLabel = (value) => SERVICE_TYPE_LABELS[value] || value;

// Style objects for buttons
const styles = {
  confirmButton: {
    display: "block",
    margin: "20px auto",
    padding: "15px",
    width: "100%",
    backgroundColor: UMSSBlue,
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "1.1rem",
    cursor: "pointer",
    fontFamily: "Quicksand, sans-serif",
    transition: "background-color 0.2s ease",
  },
  googleCalendarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "2px solid #4285F4",
    color: "#000",
    borderRadius: "10px",
    fontSize: "1.1rem",
    fontWeight: "600",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.1s ease, background-color 0.2s ease",
    fontFamily: "Quicksand, sans-serif",
  },
  googleCalendarButtonHover: {
    backgroundColor: UMSSBlueDark,
    color: "#fff",
  },
  appleCalendarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "12px 24px",
    backgroundColor: "#000",
    border: "2px solid #000",
    color: "#fff",
    borderRadius: "10px",
    fontSize: "1.1rem",
    fontWeight: "600",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.1s ease, background-color 0.2s ease",
    fontFamily: "Quicksand, sans-serif",
  },
  appleCalendarButtonHover: {
    backgroundColor: "#333",
  },
};

function Confirmation({
  appointmentId,
  location,
  selectedDate,
  selectedTime,
  selectedServiceType,
  patientData,
  verifiedPatientId,
  onConfirmed
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [hoverGoogle, setHoverGoogle] = useState(false);
  const [hoverApple, setHoverApple] = useState(false);
  const [bookingStatus, setBookingStatus] = useState({
    isLoading: false,
    error: null,
    success: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize EmailJS when component mounts
  useEffect(() => {
    // EmailJS doesn't require initialization with recent versions
    // The init method is only needed for older versions
    const userID = process.env.REACT_APP_EMAILJS_USER_ID;
    if (!userID) {
      console.warn("EmailJS User ID is not configured in environment variables");
    }
  }, []);

  // Function to send appointment details via email using EmailJS
  const sendAppointmentEmail = (appointmentInfo) => {
    const { patient, location, selectedDate, selectedTime, rescheduleURL, cancelURL } = appointmentInfo;
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userID = process.env.REACT_APP_EMAILJS_USER_ID;
  
    console.log("EmailJS Config:", {
      serviceID,
      templateID,
      userID: userID ? "exists" : "missing"
    });
  
    if (!serviceID || !templateID || !userID) {
      console.error("Missing EmailJS configuration. Please check your environment variables.");
      return Promise.reject(new Error("Email service not properly configured"));
    }
  
    if (!patient || !patient.email) {
      console.error("Patient email is missing.");
      return Promise.reject(new Error("Patient email is missing"));
    }
  
    const templateParams = {
      to_email: patient.email,
      patient_name: `${patient.firstName} ${patient.lastName}`,
      appointment_date: selectedDate ? selectedDate.toLocaleDateString() : "N/A",
      appointment_time: selectedTime,
      location_name: location.name,
      location_address: location.address,
      reschedule_url: rescheduleURL,
      cancel_url: cancelURL,
    };
  
    return emailjs
      .send(serviceID, templateID, templateParams, userID)
      .then((response) => {
        console.log("Email sent successfully:", response.status, response.text);
        return response;
      })
      .catch((error) => {
        console.error("Failed to send email. Details:", error);
        throw error;
      });
  };

  const handleConfirm = async () => {
    if (!patientData) {
      console.error("No patient data available for booking!");
      setBookingStatus({
        isLoading: false,
        error: "Missing patient information",
        success: false,
      });
      return;
    }
  
    setBookingStatus({ isLoading: true, error: null, success: false });
  
    try {
      // Book the appointment with the appointmentId, location.id, and patient data
      const result = await bookAppointment(
        appointmentId,
        location.id,
        patientData.toJSON ? patientData.toJSON() : patientData,
        verifiedPatientId // Pass the ID of the verified patient
      );
  
      if (result.success) {
        setConfirmed(true);
        setBookingStatus({
          isLoading: false,
          error: null,
          success: true,
          patientId: result.patientId,
        });
  
        // Construct domain and URLs for reschedule and cancel actions.
        const domain = "https://umss-appts-d5ea54e435bd.herokuapp.com";
        const rescheduleURL = `${domain}/reschedule?appointmentId=${appointmentId}`;
        const cancelURL = `${domain}/cancel?appointmentId=${appointmentId}`;
  
        // Send the appointment confirmation email with all required details.
        try {
          await sendAppointmentEmail({
            patient: patientData,
            location,
            selectedDate,
            selectedTime,
            rescheduleURL,
            cancelURL,
          });
          console.log("Email notification sent successfully");
        } catch (emailError) {
          console.error("Email notification failed:", emailError);
          console.warn("Appointment booked successfully, but failed to send email notification:", emailError);
        }
  
        // Call the onConfirmed callback to update parent component state
        if (onConfirmed) onConfirmed();
      } else {
        throw result.error || new Error("Failed to book appointment");
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      setBookingStatus({
        isLoading: false,
        error: error.message || "An error occurred while booking your appointment",
        success: false,
      });
    }
  };


  const handleAddGoogleCalendar = () => {
    const appointmentDurationMinutes = 30; // For example, a 30-minute appointment
    
    // Parse selectedTime (assumed format: "h:mm A", e.g., "10:00 AM")
    const [time, meridiem] = selectedTime.split(" ");
    let [hoursStr, minutesStr] = time.split(":");
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }
    
    // Construct a Date object for the start time using the selected date and time.
    const startDateTime = new Date(selectedDate);
    startDateTime.setHours(hours, minutes, 0, 0);
    
    // Compute the end time
    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + appointmentDurationMinutes);
    
    // Format dates for Google Calendar: YYYYMMDDTHHMMSSZ.
    const formatForGoogle = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };
    const startStr = formatForGoogle(startDateTime);
    const endStr = formatForGoogle(endDateTime);
    
    // Build event details
    const title = encodeURIComponent("Appointment at " + (location.name || ""));
    const details = encodeURIComponent("Your appointment has been scheduled.");
    const locationStr = encodeURIComponent(location.address || "");
    
    // Construct the Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startStr}/${endStr}&details=${details}&location=${locationStr}`;
    
    window.open(googleCalendarUrl, "_blank");
  };

  const handleAddAppleCalendar = () => {
    const appointmentDurationMinutes = 30;
    
    const [time, meridiem] = selectedTime.split(" ");
    let [hoursStr, minutesStr] = time.split(":");
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }
    
    const startDateTime = new Date(selectedDate);
    startDateTime.setHours(hours, minutes, 0, 0);
    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + appointmentDurationMinutes);
    
    const formatICS = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };
    const startICS = formatICS(startDateTime);
    const endICS = formatICS(endDateTime);
    
    const title = "Appointment at " + (location.name || "");
    const description = "Your appointment has been scheduled.";
    const locationStr = location.address || "";
    
    const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startICS}
DTEND:${endICS}
SUMMARY:${title}
DESCRIPTION:${description}
LOCATION:${locationStr}
END:VEVENT
END:VCALENDAR
    `.trim();
    
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "appointment.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        maxWidth: "600px",
        margin: "0 auto",
        padding: "20px",
        border: "1px solid #ccc",
        borderRadius: "10px",
        backgroundColor: "#f9f9f9",
        fontFamily: "Quicksand, sans-serif",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          margin: "1rem 0 3rem",
          color: "#333",
        }}
      >
        Confirm Your Appointment
      </h2>
      <p style={{ color: "#555" }}>
        <strong>Location:</strong> {location ? location.name : "Not selected"}
      </p>
      {location && location.address && (
        <p style={{ color: "#555" }}>
          <strong>Address:</strong> {location.address}
        </p>
      )}
      <p style={{ color: "#555" }}>
        <strong>Date:</strong>{" "}
        {selectedDate ? selectedDate.toLocaleDateString() : "Not selected"}
      </p>
      <p style={{ color: "#555" }}>
        <strong>Time:</strong> {selectedTime}
      </p>
      {selectedServiceType && (
        <p style={{ color: "#555" }}>
          <strong>Service:</strong> {getServiceTypeLabel(selectedServiceType)}
        </p>
      )}

      {/* Show booking error if any */}
      {bookingStatus.error && (
        <div style={{ 
          padding: "0.75rem", 
          backgroundColor: "#ffebee", 
          color: "#c62828", 
          borderRadius: "8px",
          margin: "1rem 0"
        }}>
          <p style={{ margin: 0 }}>{bookingStatus.error}</p>
        </div>
      )}

      {!confirmed ? (
        <button
          onClick={handleConfirm}
          disabled={bookingStatus.isLoading}
          style={{
            ...styles.confirmButton,
            opacity: bookingStatus.isLoading ? 0.7 : 1,
            cursor: bookingStatus.isLoading ? "not-allowed" : "pointer",
          }}
        >
          {bookingStatus.isLoading ? "Processing..." : "Confirm Appointment"}
        </button>
      ) : (
        <>
          <p
            style={{
              marginTop: "40px",
              textAlign: "center",
              fontSize: "1.2rem",
              fontWeight: "500",
              color: UMSSBlue,
            }}
          >
            Please check your email for confirmation!
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "20px",
            }}
          >
            <button
              onClick={handleAddGoogleCalendar}
              style={{
                ...styles.googleCalendarButton,
                ...(hoverGoogle ? styles.googleCalendarButtonHover : {}),
              }}
              onMouseEnter={() => setHoverGoogle(true)}
              onMouseLeave={() => setHoverGoogle(false)}
            >
              <img
                src={GoogleSvg}
                alt="Google Calendar"
                style={{ width: "20px", height: "20px" }}
              />
              Add to Google Calendar
            </button>
            <button
              onClick={handleAddAppleCalendar}
              style={{
                ...styles.appleCalendarButton,
                ...(hoverApple ? styles.appleCalendarButtonHover : {}),
              }}
              onMouseEnter={() => setHoverApple(true)}
              onMouseLeave={() => setHoverApple(false)}
            >
              <img
                src={AppleSvg}
                alt="Apple Calendar"
                style={{ width: "20px", height: "20px", filter: "invert(1)" }}
              />
              Add to Apple Calendar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Confirmation;